/**
 * A FEDERATED CONNECTION (PLUGINS.md; PRODUCT.md §4, R4) — connect a user's OWN outside system with NO stored secret,
 * for any connection type a PLUGIN contributes, as a 3-step stepper:
 *   1. Trust   — download the plugin's template and deploy it in THEIR system (the platform's OIDC issuer + their
 *                workspace's audience + ONE identity trusting only what they chose). Shows issuer / audience / subjects
 *                so a security reviewer sees exactly what is trusted.
 *   2. Details — name the connection + the plugin's fields (e.g. the role it outputs, a default region).
 *   3. Test    — the platform mints this workspace's identity, the plugin exchanges it and checks → TESTED_OK.
 * Registry mode (the Connections tab). Revoking access = deleting what they deployed.
 *
 * PLATFORM governance (EXPERT-NETWORKS §7c L4 — a corporation gives each department a room), the same for every type:
 *   - who uses it: everyday work (``workload``) or the ORGANIZATION connection (``admin`` — one per organization,
 *     workspace OWNER only, used only in a step a person approved);
 *   - what it trusts: this room only (default when opened in a room) or, for a workload identity, the whole workspace;
 *   - four-eyes (``SECOND_APPROVER``, on by default): whoever ran an organization step can't approve it.
 * Saved with the connection (``TIER`` + ``TRUST_ROOMS``) so the Test mints exactly that identity. The vendor's words and
 * fields come from the plugin's ``wizard`` spec — this component names no vendor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Cloud, Download, CheckCircle2, XCircle } from 'lucide-react';
import {
  createSecretsRegistrySecret, updateSecretsRegistrySecret, testSecretsRegistrySecret, getConnectionTemplate,
} from '../../services/toolsApi';
import ConnectionAddOns from './ConnectionAddOns.jsx';

const STEPS = ['Trust', 'Details', 'Test'];

/** PURE — a registry row → the wizard's starting state for that connection type. */
export function mapRegistryFederatedToInitial(secret, spec) {
  if (!secret || typeof secret !== 'object') return undefined;
  const c = secret.config && typeof secret.config === 'object' ? secret.config : {};
  const values = {};
  for (const f of (spec?.wizard?.fields || [])) values[f.id] = String(c[f.id] || '');
  return { secretId: String(secret.secretId || ''), instanceLabel: String(secret.secretName || ''), values,
    version: secret.version, tier: c.TIER === 'admin' ? 'admin' : 'workload',
    trustRooms: Array.isArray(c.TRUST_ROOMS) ? c.TRUST_ROOMS : [],
    ...(Array.isArray(c.ADD_ONS) ? { addOns: c.ADD_ONS } : {}),
    secondApprover: String(c.SECOND_APPROVER || '').toLowerCase() !== 'off' };
}

/** PURE — the template request for a choice (tier + trust) in a given registry scope. */
export function trustRequest({ tier, trust, registryScope, addOns = [] }) {
  const s = registryScope || {};
  const inRoom = s.kind === 'POOL' && s.networkId && s.poolId;
  // ONE-STEP setup: the everyday (workload) template also carries the add-ons kept on; the organization one never does
  const extra = addOns.length ? { addOns } : {};
  if (tier === 'admin') return inRoom ? { tier, networkId: s.networkId, poolId: s.poolId } : null;
  if (trust === 'room' && inRoom) return { tier: 'workload', networkId: s.networkId, poolId: s.poolId, ...extra };
  return { tier: 'workload', scope: 'workspace', ...extra };
}

/** PURE — does a field's value pass its pattern (no pattern / not required + empty = ok)? */
export function fieldOk(f, v) {
  const s = String(v || '').trim();
  if (!s) return !f.required;
  if (!f.pattern) return true;
  try { return new RegExp(f.pattern).test(s); } catch { return true; }
}

export default function FederatedConnectionWizard({ open, spec, initialEntry, registryScope, onRegistryCreated, onCancel }) {
  const w = spec?.wizard || {};
  const fields = useMemo(() => w.fields || [], [w.fields]);
  const editing = !!initialEntry?.secretId;
  const [step, setStep] = useState(editing ? 1 : 0);
  const [tpl, setTpl] = useState(null);
  const [label, setLabel] = useState(initialEntry?.instanceLabel || '');
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.id,
    initialEntry?.values?.[f.id] || f.default || ''])));
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(null);
  const inRoom = registryScope?.kind === 'POOL' && !!registryScope?.poolId;
  const [tier, setTier] = useState(initialEntry?.tier || 'workload');
  const [trust, setTrust] = useState(inRoom ? 'room' : 'workspace');
  const [secondApprover, setSecondApprover] = useState(initialEntry?.secondApprover !== false);
  // the add-ons the connection's OWN template can carry (``inTemplate``) — kept ON by default: one stack, one step
  const inTemplate = useMemo(() => (spec?.addOns || []).filter((a) => a.inTemplate), [spec?.addOns]);
  const [addOnsOn, setAddOnsOn] = useState(() => initialEntry?.addOns || inTemplate.map((a) => a.id));
  const req = trustRequest({ tier, trust, registryScope, addOns: tier === 'admin' ? [] : addOnsOn });
  const reqKey = JSON.stringify(req);
  useEffect(() => {
    if (!open || !spec?.id) return;
    setTpl(null);
    if (!req) return;
    getConnectionTemplate(spec.id, req).then(setTpl).catch((e) => setError(e?.message || "Couldn't load the template"));
  }, [open, reqKey, spec?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !spec) return null;
  const allOk = fields.every((f) => fieldOk(f, values[f.id]));
  const download = () => {
    if (!tpl?.template) return;
    const url = URL.createObjectURL(new Blob([tpl.template], { type: 'text/yaml' }));
    const a = document.createElement('a');
    a.href = url; a.download = tpl.fileName || w.templateFile || 'stepflow-connection.yaml'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const saveAndTest = async () => {
    setBusy('test'); setError(''); setResult(null);
    try {
      const config = { ...Object.fromEntries(fields.map((f) => [f.id, String(values[f.id] || '').trim()])), TIER: tier,
        TRUST_ROOMS: Array.isArray(tpl?.trustRooms) ? tpl.trustRooms : [],
        ...(inTemplate.length ? { ADD_ONS: tier === 'admin' ? [] : addOnsOn } : {}),
        ...(tier === 'admin' ? { SECOND_APPROVER: secondApprover ? 'on' : 'off' } : {}) };
      let row = saved;
      if (editing && !row) {
        const d = await updateSecretsRegistrySecret(initialEntry.secretId, { secretName: label.trim(), config },
          typeof initialEntry.version === 'number' ? { ifMatch: String(initialEntry.version) } : {});
        row = d?.secret;
      } else if (!row) {
        // the organization (admin) connection belongs to the whole workspace — one per organization
        const d = await createSecretsRegistrySecret({ toolName: spec.id, secretName: label.trim(), config,
          plaintextSecrets: {}, scope: tier === 'admin' ? { kind: 'TENANT' } : registryScope });
        row = d?.secret;
      }
      if (!row?.secretId) throw new Error('Unexpected response from the registry');
      setSaved(row);
      const t = await testSecretsRegistrySecret(row.secretId, {});
      setResult(t);
      setStep(2);
    } catch (e) { setError(e?.message || "Couldn't save the connection"); }
    setBusy('');
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true"
      data-testid="fed-wizard" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="flex items-start gap-2 border-b border-gray-200 px-6 py-4">
          <Cloud className="mt-0.5 h-5 w-5 text-gray-500" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-900">{editing ? 'Edit' : 'Connect'} {w.title || spec.label || spec.id}</h2>
            {w.intro ? <p className="mt-1 text-xs text-gray-600">{w.intro}</p> : null}
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            className="px-2.5 py-1 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100">×</button>
        </div>
        <ol className="flex gap-1 px-6 pt-3" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li key={s} data-testid={`fed-step-${i}`} aria-current={i === step ? 'step' : undefined}
              className={`flex-1 rounded-md px-2 py-1 text-center text-[11px] font-medium ${i === step ? 'bg-indigo-600 text-white'
                : i < step ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-50 text-gray-400'}`}>{i + 1}. {s}</li>
          ))}
        </ol>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-6 py-4 text-sm">
          {step === 0 ? (
            <div data-testid="fed-trust">
              <fieldset className="mb-3 space-y-1 text-xs" data-testid="fed-tier">
                <legend className="font-medium text-gray-700">Who uses this connection</legend>
                <label className="flex items-start gap-2"><input type="radio" name="fed-tier" checked={tier === 'workload'}
                  onChange={() => setTier('workload')} data-testid="fed-tier-workload" />
                  <span>{w.tiers?.workload || 'Everyday work'}</span></label>
                <label className="flex items-start gap-2"><input type="radio" name="fed-tier" checked={tier === 'admin'}
                  onChange={() => setTier('admin')} data-testid="fed-tier-admin" />
                  <span>{w.tiers?.admin || 'The organization connection — only the workspace owner sets it up, and agents use it only in a step a person approved'}</span></label>
              </fieldset>
              {tier === 'workload' ? (
                <fieldset className="mb-3 space-y-1 text-xs" data-testid="fed-trust-scope">
                  <legend className="font-medium text-gray-700">Trusted by</legend>
                  {inRoom ? (
                    <label className="flex items-start gap-2"><input type="radio" name="fed-trust" checked={trust === 'room'}
                      onChange={() => setTrust('room')} data-testid="fed-trust-room" />
                      <span>This room only (recommended — e.g. one room per department)</span></label>
                  ) : null}
                  <label className="flex items-start gap-2"><input type="radio" name="fed-trust" checked={trust === 'workspace'}
                    onChange={() => setTrust('workspace')} data-testid="fed-trust-workspace" />
                    <span>Every room in this workspace (fine for one person)</span></label>
                </fieldset>
              ) : null}
              {tier === 'admin' ? (
                <label className="mb-3 flex items-start gap-2 text-xs" data-testid="fed-second-approver">
                  <input type="checkbox" checked={secondApprover} onChange={(e) => setSecondApprover(e.target.checked)}
                    data-testid="fed-second-approver-toggle" />
                  <span>A second person approves — whoever started the run can't approve its organization step (turn
                    off only if you work alone)</span></label>
              ) : null}
              {tier !== 'admin' && inTemplate.map((a) => (
                <label key={a.id} className="mb-3 flex items-start gap-2 text-xs" data-testid={`fed-addon-${a.id}`}>
                  <input type="checkbox" checked={addOnsOn.includes(a.id)} data-testid={`fed-addon-toggle-${a.id}`}
                    onChange={(e) => setAddOnsOn((cur) => (e.target.checked ? [...new Set([...cur, a.id])]
                      : cur.filter((x) => x !== a.id)))} />
                  <span><span className="font-medium">{a.label}</span>{a.intro ? ` — ${a.intro}` : ''}</span></label>
              ))}
              {!req ? (
                <p className="mb-2 text-xs text-amber-700" data-testid="fed-admin-needs-room">Open this from the room that
                  manages your organization — the organization connection trusts named rooms only.</p>
              ) : null}
              <p className="text-xs text-gray-600">{w.deploy || 'Deploy this template in your system. '}{w.deployTier?.[tier] || ''}</p>
              <button type="button" onClick={download} disabled={!tpl?.template} data-testid="fed-template-download"
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                <Download className="h-3.5 w-3.5" /> Download template
              </button>
              {tpl ? (
                <dl className="mt-3 grid grid-cols-[6rem_1fr] gap-x-2 gap-y-1 text-[11px]" data-testid="fed-trust-facts">
                  <dt className="text-gray-500">Issuer</dt><dd className="font-mono break-all">{tpl.issuerUrl}</dd>
                  <dt className="text-gray-500">Audience</dt><dd className="font-mono">{tpl.audience}</dd>
                  <dt className="text-gray-500">Trusted</dt><dd className="font-mono break-all">{
                    (Array.isArray(tpl.subjects) ? tpl.subjects : [tpl.subject]).join(', ')}</dd>
                </dl>
              ) : null}
            </div>
          ) : null}
          {step === 1 ? (
            <div className="space-y-3" data-testid="fed-details">
              <label className="block text-xs font-medium text-gray-700">Connection name
                <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="fed-label"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" placeholder={w.namePlaceholder || ''} />
              </label>
              {fields.map((f) => (
                <label key={f.id} className="block text-xs font-medium text-gray-700">{f.label || f.id}
                  <input value={values[f.id] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                    data-testid={`fed-field-${f.id}`} placeholder={f.placeholder || ''}
                    className={`mt-1 block rounded-md border border-gray-300 px-3 py-1.5 ${f.short ? 'w-40 text-sm' : 'w-full'} ${f.mono ? 'font-mono text-xs' : 'text-sm'}`} />
                  {values[f.id] && !fieldOk(f, values[f.id]) ? <span className="text-[11px] text-rose-600">{f.invalid || "That doesn't look right."}</span> : null}
                </label>
              ))}
            </div>
          ) : null}
          {step === 2 && result ? (
            <div data-testid="fed-test-result" className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${result.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
              {result.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <XCircle className="mt-0.5 h-4 w-4" />}
              <span>{result.message}</span>
            </div>
          ) : null}
          {step === 2 && result?.ok ? (
            <ConnectionAddOns spec={spec} secretId={saved?.secretId}
              included={tier === 'admin' ? [] : addOnsOn} autoCheck={tier === 'admin' ? [] : addOnsOn} />
          ) : null}
          {error ? <div className="text-xs text-rose-600" data-testid="fed-error">{error}</div> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3">
          {step > 0 ? <button type="button" onClick={() => setStep(step - 1)} className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100">Back</button> : null}
          {step === 0 ? <button type="button" onClick={() => setStep(1)} data-testid="fed-next"
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700">{w.deployedLabel || "I've deployed it"}</button> : null}
          {step === 1 ? <button type="button" onClick={saveAndTest} disabled={!label.trim() || !allOk || !!busy} data-testid="fed-save-test"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save &amp; test</button> : null}
          {step === 2 ? (result?.ok
            ? <button type="button" data-testid="fed-done" onClick={() => onRegistryCreated?.(saved)}
              className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Done</button>
            : <button type="button" onClick={saveAndTest} disabled={!!busy}
              className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Test again</button>) : null}
        </div>
      </div>
    </div>
  );
}
